import type { MoisComptable } from '../api/compta'
import { fcfa } from './Format'

/**
 * Les graphiques du tableau de bord comptable — TRANSPOSITION de `svgLine` et
 * `svgDonut`.
 *
 * Le portage n'en avait que trois sur quatre, et avait remplacé l'ANNEAU du
 * « CA par type » par des barres horizontales. Surtout, le graphique de LIGNES
 * manquait entièrement : c'est le seul qui superpose GMV, CA net et charges
 * sur le même axe, et donc le seul qui montre l'écart entre le volume traité
 * et ce qu'on gagne réellement. En modèle agent, c'est LA courbe à regarder.
 *
 * ⚠️ Les couleurs viennent de variables CSS, jamais d'hexadécimal en dur :
 * elles doivent suivre le thème comme le reste de la console.
 */
const L = 320
const H = 140
const M = 22

export function GraphiqueLignes({ mois }: { mois: MoisComptable[] }) {
  if (mois.length < 1) return <div className="ch-empty">Pas encore de donnees</div>

  const lignes = mois.map((m) => ({ gmv: m.gmv, ca: m.ca, ch: m.charges }))
  const max = Math.max(1, ...lignes.flatMap((r) => [r.gmv, r.ca, r.ch]))
  const n = lignes.length
  /* Un seul point se pose au MILIEU : la formule générale diviserait par zéro
     et le tracé disparaîtrait au premier mois d'activité. */
  const x = (i: number) => M + (n > 1 ? (i * (L - 2 * M)) / (n - 1) : (L - 2 * M) / 2)
  const y = (v: number) => H - M - (v / max) * (H - 2 * M)
  const trace = (cle: 'gmv' | 'ca' | 'ch') =>
    lignes.map((r, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(r[cle]).toFixed(1)}`).join(' ')

  return (
    <svg viewBox={`0 0 ${L} ${H}`} className="chart-svg">
      <line x1={M} y1={H - M} x2={L - M} y2={H - M} stroke="var(--n200)" strokeWidth="1" />
      {(
        [
          ['gmv', '--n400'],
          ['ca', '--green'],
          ['ch', '--danger'],
        ] as const
      ).map(([cle, couleur]) => (
        <path
          key={cle}
          d={trace(cle)}
          fill="none"
          stroke={`var(${couleur})`}
          strokeWidth="2.2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      ))}
      {lignes.map((r, i) => (
        <circle
          key={i}
          cx={x(i).toFixed(1)}
          cy={y(r.ca).toFixed(1)}
          r="2.4"
          fill="var(--bg-card, var(--surface))"
          stroke="var(--n700)"
          strokeWidth="0"
        />
      ))}
    </svg>
  )
}

export function LegendeLignes() {
  return (
    <div className="chart-legend">
      <span className="ch-leg">
        <span style={{ background: 'var(--n400)' }} />
        GMV
      </span>
      <span className="ch-leg">
        <span style={{ background: 'var(--green)' }} />
        CA net
      </span>
      <span className="ch-leg">
        <span style={{ background: 'var(--danger)' }} />
        Charges
      </span>
    </div>
  )
}

/**
 * Anneau de répartition. Les parts sont posées dans le sens horaire à partir
 * de midi (d'où le `-π/2` de départ) — l'attente pour un camembert.
 */
export function Anneau({
  parts,
}: {
  parts: Array<{ libelle: string; valeur: number; couleur: string }>
}) {
  const total = parts.reduce((s, p) => s + p.valeur, 0)
  if (!total) return <div className="ch-empty">—</div>

  const R = 52
  const r = 31
  const cx = 60
  const cy = 60
  let a = -Math.PI / 2

  const segments = parts.map((p) => {
    const angle = (p.valeur / total) * Math.PI * 2
    const a2 = a + angle
    // Au-delà d'un demi-tour, l'arc SVG doit être marqué « grand » — sans ce
    // drapeau, une part majoritaire se dessine en petit arc, à l'envers.
    const grand = angle > Math.PI ? 1 : 0
    const x1 = cx + R * Math.cos(a)
    const y1 = cy + R * Math.sin(a)
    const x2 = cx + R * Math.cos(a2)
    const y2 = cy + R * Math.sin(a2)
    const x3 = cx + r * Math.cos(a2)
    const y3 = cy + r * Math.sin(a2)
    const x4 = cx + r * Math.cos(a)
    const y4 = cy + r * Math.sin(a)
    a = a2
    return {
      cle: p.libelle,
      couleur: p.couleur,
      d:
        `M${x1.toFixed(1)} ${y1.toFixed(1)} A${R} ${R} 0 ${grand} 1 ${x2.toFixed(1)} ${y2.toFixed(1)} ` +
        `L${x3.toFixed(1)} ${y3.toFixed(1)} A${r} ${r} 0 ${grand} 0 ${x4.toFixed(1)} ${y4.toFixed(1)} Z`,
    }
  })

  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
      <svg viewBox="0 0 120 120" style={{ width: 118, flex: 'none' }}>
        {segments.map((s) => (
          <path key={s.cle} d={s.d} fill={`var(${s.couleur})`} />
        ))}
      </svg>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {parts.map((p) => (
          <div className="ch-leg" key={p.libelle}>
            <span style={{ background: `var(${p.couleur})` }} />
            {p.libelle} <b style={{ marginLeft: 'auto' }}>{fcfa(p.valeur)}</b>
          </div>
        ))}
      </div>
    </div>
  )
}
